import {EditorView, WidgetType} from '@codemirror/view'
import type {Element} from 'hast'

import {hastPropertiesToHtmlAttributes} from '../../language-tools/html/hast-utils'

/**
 * Widget that renders void (childless) HTML elements like images and horizontal rules.
 *
 * This widget is used by the markdown renderer to display elements that have no
 * child content and must be rendered as complete replacement decorations rather than
 * wrapping decorations.
 *
 * Key feature: The `ignoreEvent()` method returns false (explicitly allows events to bubble),
 * which enables event handlers in other extensions (like image-click) to detect clicks on
 * rendered elements and handle them appropriately.
 */
export class VoidElementWidget extends WidgetType {
  #node: Element
  #className: string | undefined

  /**
   * @param node - hast node to render as a void element. Any children will be ignored.
   * @param className - Optional CSS class name to apply in addition to node.properties.class
   */
  constructor(node: Element, className?: string) {
    super()
    this.#node = node
    this.#className = className
  }

  override toDOM(_view: EditorView) {
    // We could use hast-util-to-dom to handle the element creation for us, but it's probably not worth the added dependency
    const element = document.createElement(this.#node.tagName)
    for (const [key, value] of Object.entries(hastPropertiesToHtmlAttributes(this.#node.properties)))
      element.setAttribute(key, String(value))
    if (this.#className) element.classList.add(this.#className)
    return element
  }

  override eq(widget: WidgetType): boolean {
    return (
      widget instanceof VoidElementWidget &&
      widget.#node.tagName === this.#node.tagName &&
      JSON.stringify(widget.#node.properties) === JSON.stringify(this.#node.properties) &&
      widget.#className === this.#className
    )
  }

  /**
   * Allow events to bubble so that CodeMirror can handle them natively.
   */
  override ignoreEvent() {
    return false
  }
}
